import React, { useState } from "react";
import { MoreHorizontal, Calendar, Clock } from "lucide-react";
import GitHubHeatmap from "@/components/ui/GitHubHeatmap";
import ProfileEditModal from "@/components/community/ProfileEditModal";
import UserPostsList from "@/components/community/UserPostsList";
import { IPost } from "@/lib/community";
import PostModal from "@/components/community/PostModal";
import { useParams } from "react-router";
import { useUserById } from "@/hooks/useUserById";
import { useUser } from "@supabase/auth-helpers-react";
import CustomAvatar from "../components/community/CustomAvatar";
import { CurrentLevelInfo } from "../components/community/CurrentLevelInfo";
import { useTranslation } from "react-i18next";
import { getAccountAge } from "@/utils/getAccountAge";

export default function ProfileComponent() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const currentUser = useUser();
  const user = useUserById(userId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<IPost | null>(null);

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="px-4 md:px-0 mx-auto w-full grid grid-cols-12 md:gap-4 lg:gap-12 items-start">
      {/* Activity Section */}
      <div className="mt-4  md:mt-0 col-span-12 sm:col-span-8 order-2 sm:order-1">
        <div className="bg-background p-6 mb-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-foreground">
              {t("community.profileComponent.activity")}
            </h2>
            <button className="pt-2 hover:bg-gray-100 rounded-full">
              <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          {/* Heatmap */}
          <GitHubHeatmap userId={userId} />
        </div>

        {/* Memberships */}
        <div className="pt-6 bg-background p-4 md:p-6 mb-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium mb-4">
            {t("community.profileComponent.posts")}
          </h3>
          <UserPostsList onPostClick={setSelectedPost} userId={userId} />
          {selectedPost && (
            <PostModal
              post={selectedPost}
              onClose={() => setSelectedPost(null)}
              setSelectedPostToNull={() => setSelectedPost(null)}
            />
          )}
        </div>
      </div>

      {/* Profile Section */}
      <div className="bg-background rounded-lg shadow-sm p-4 pt-10 col-span-12 sm:col-span-4 order-1 sm:order-2 border">
        <div className="flex flex-col items-start w-full">
          {/* Profile Image */}
          <div className="mx-auto">
            <CustomAvatar
              userData={user}
              size="xl"
              showProgress
              className="mx-auto"
            />
          </div>

          {/* Profile Info */}
          <div className="w-full ml-0 md:mt-8 h-auto">
            <div className="ml-2">
              <h1 className="text-2xl font-bold text-foreground mb-1">
                {user?.username}
              </h1>
              {user?.user_rating?.score && (
                <CurrentLevelInfo score={user.user_rating.score} />
              )}
            </div>
            <hr className="my-2" />
            <div className="flex flex-col ml-2 text-sm text-gray-600 gap-2">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <span>
                  {t("community.profileComponent.joined", {
                    days: getAccountAge(user?.created_at),
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        {userId === currentUser.id && (
          <>
            <div className="mt-4 text-center">
              <button
                onClick={openModal}
                className="w-full py-3 px-4 border-2 border-gray-300 text-gray-600 font-semibold text-sm tracking-wide uppercase rounded-lg hover:border-gray-400 hover:text-gray-700 transition-colors duration-200"
              >
                {t("community.profileComponent.editProfile")}
              </button>
            </div>
            {isModalOpen && <ProfileEditModal onClose={closeModal} />}
          </>
        )}
      </div>
    </div>
  );
}
